import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { Spinner, vibrantTheme } from "./common";
import { airlineLogo } from "../lib/assets";
import PopSectionTabs from "./PopSectionTabs";

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3.5 py-2 text-sm font-700 border transition-colors ${active ? "bg-primary text-white border-primary" : "border-zinc-300 bg-white text-zinc-700 hover:border-primary"}`}>
    {children}
  </button>
);

/* Light pastel shades rotated per card, keyed by IATA so each airline keeps its shade. */
const LIGHT_SHADES = ["#EFF6FF", "#ECFDF5", "#F5F3FF", "#FFF7ED", "#FEFCE8", "#FDF2F8", "#F0FDFA"];
const shadeFor = (s) => LIGHT_SHADES[[...(s || "")].reduce((n, c) => n + c.charCodeAt(0), 0) % LIGHT_SHADES.length];

export function AirlineCard({ a, accent }) {
  const [imgOk, setImgOk] = useState(true);
  const logo = airlineLogo(a.iata);
  return (
    <Link to={`/airlines/${a.id}`} data-testid={`airline-pop-card-${a.iata}`}
      className="group border border-zinc-300 p-4 flex gap-3 hover:-translate-y-px hover:border-primary transition-[transform,border-color]"
      style={{ backgroundColor: shadeFor(a.iata + a.name) }}>
      <div className="w-14 h-14 shrink-0 border border-zinc-200 bg-white flex items-center justify-center overflow-hidden">
        {logo && imgOk
          ? <img src={logo} alt="" className="max-w-full max-h-full object-contain" onError={() => setImgOk(false)} />
          : <span className="font-mono font-700 text-base" style={{ color: accent }}>{a.iata || "—"}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="font-700 text-base text-zinc-900 group-hover:text-primary transition-colors truncate">{a.name}</div>
          <span className="font-mono text-sm font-700 text-white px-2 py-0.5 shrink-0" style={{ backgroundColor: accent }}>#{a.rank}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-zinc-600 mt-1">
          <span className="font-mono font-700">{a.iata}</span>
          {a.share && <span className="inline-flex items-center gap-1 font-700" style={{ color: accent }}><TrendingUp className="w-4 h-4" />{a.share}</span>}
          <span className="inline-flex items-center gap-0.5 truncate"><MapPin className="w-4 h-4 shrink-0" />{a.country}</span>
        </div>
        {a.regions?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {a.regions.slice(0, 3).map((r) => <span key={r} className="text-sm bg-white/80 border border-zinc-200 text-zinc-700 px-2 py-0.5">{r}</span>)}
            {a.regions.length > 3 && <span className="text-sm text-zinc-500">+{a.regions.length - 3}</span>}
          </div>
        )}
        {a.note && <div className="text-sm text-amber-700 mt-1.5">{a.note}</div>}
      </div>
    </Link>
  );
}

const Grid = ({ items, accent }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
    {items.map((a) => <AirlineCard key={a.iata + a.id} a={a} accent={accent} />)}
  </div>
);

const TierHeading = ({ children, accent }) => (
  <h3 className="font-heading font-900 text-lg mt-6 mb-3 flex items-center gap-2">
    <span className="w-2 h-5 inline-block" style={{ backgroundColor: accent }} /> {children}
  </h3>
);

export default function PopularAirlines() {
  const [data, setData] = useState(null);
  const [usRegion, setUsRegion] = useState("");
  const [euRegion, setEuRegion] = useState("");
  useEffect(() => { api.get("/airlines-popular").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  const usTheme = vibrantTheme("pop-us"), euTheme = vibrantTheme("pop-europe"), glTheme = vibrantTheme("pop-global");

  const usFiltered = useMemo(() => {
    if (!data || !usRegion) return null;
    const codes = data.us.regions[usRegion] || [];
    const all = [...data.us.major, ...data.us.low_cost, ...data.us.regional];
    return codes.map((c) => all.find((a) => a.iata === c)).filter(Boolean);
  }, [data, usRegion]);

  const euItems = useMemo(() => {
    if (!data) return [];
    return euRegion ? data.europe.items.filter((a) => a.euro_regions?.includes(euRegion)) : data.europe.items;
  }, [data, euRegion]);

  if (data === null) return <Spinner />;
  if (data === false) return <p className="text-muted-foreground py-10">Could not load the popularity directory.</p>;

  return (
    <div data-testid="popular-airlines-view">
      <PopSectionTabs prefix="airlines" />
      <div className="space-y-14">
      <section id="us" data-testid="us-airlines-section">
        <div className="text-white px-5 py-4 flex items-center justify-between flex-wrap gap-2 scroll-mt-32" style={{ backgroundColor: usTheme.bg }}>
          <h2 className="font-heading font-900 text-2xl">US Airlines</h2>
          <span className="text-xs font-mono text-white/85">Ranked by domestic passenger share (BTS)</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-4 mb-2">
          <Chip active={!usRegion} onClick={() => setUsRegion("")} testid="us-region-chip-all">All regions</Chip>
          {Object.keys(data.us.regions).map((r) => (
            <Chip key={r} active={usRegion === r} onClick={() => setUsRegion(r)} testid={`us-region-chip-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{r}</Chip>
          ))}
        </div>
        {usFiltered ? (
          <>
            <p className="text-sm text-muted-foreground mb-3">Airlines with the strongest presence in <strong>{usRegion}</strong>, in order of regional strength.</p>
            <Grid items={usFiltered.map((a, i) => ({ ...a, rank: i + 1 }))} accent={usTheme.bg} />
          </>
        ) : (
          <>
            <TierHeading accent={usTheme.bg}>Major U.S. Airlines</TierHeading>
            <Grid items={data.us.major} accent={usTheme.bg} />
            <TierHeading accent={usTheme.bg}>Low-Cost / Ultra-Low-Cost</TierHeading>
            <Grid items={data.us.low_cost} accent={usTheme.bg} />
            <TierHeading accent={usTheme.bg}>Regional Airlines</TierHeading>
            <p className="text-sm text-muted-foreground mb-3">These carriers operate a huge share of regional flying, usually branded as Delta Connection, American Eagle or United Express.</p>
            <Grid items={data.us.regional} accent={usTheme.bg} />
          </>
        )}
      </section>

      <section id="europe" data-testid="europe-airlines-section">
        <div className="text-white px-5 py-4 flex items-center justify-between flex-wrap gap-2 scroll-mt-32" style={{ backgroundColor: euTheme.bg }}>
          <h2 className="font-heading font-900 text-2xl">European Airlines</h2>
          <span className="text-xs font-mono text-white/85">Ranked by passenger volume &amp; network size</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-4 mb-3">
          <Chip active={!euRegion} onClick={() => setEuRegion("")} testid="eu-region-chip-all">All of Europe</Chip>
          {data.europe.regions.map((r) => (
            <Chip key={r} active={euRegion === r} onClick={() => setEuRegion(r)} testid={`eu-region-chip-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{r}</Chip>
          ))}
        </div>
        <Grid items={euItems} accent={euTheme.bg} />
      </section>

      <section id="global" data-testid="global-airlines-section">
        <div className="text-white px-5 py-4 flex items-center justify-between flex-wrap gap-2 scroll-mt-32" style={{ backgroundColor: glTheme.bg }}>
          <h2 className="font-heading font-900 text-2xl">Other Global Airlines</h2>
          <span className="text-xs font-mono text-white/85">Major carriers across Asia, the Middle East, Americas, Africa &amp; Oceania</span>
        </div>
        <div className="mt-4">
          <Grid items={data.global.items} accent={glTheme.bg} />
        </div>
      </section>
      </div>
    </div>
  );
}
